import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Footer from '@common/components/Footer/Footer';
import { PleaseWaitLoader } from '@common/components/PleaseWaitLoader/PleaseWaitLoader';
import * as Styled from './styles';
import { Button, ButtonTypes } from '@common/components';
import { publicResendOptIn } from '../../services/broadcastService';

interface IPreferenceError {
  cause?: number;
  message?: string;
  data?: { errorMessage?: string };
}

interface ISuccessContent {
  title: string;
  message: string;
  linkText: string;
  /** The action to link to (e.g., 'opt-in' or 'opt-out') */
  linkAction: string;
}

interface IInvalidTokenContent {
  title: string;
  message: string;
}

interface IBroadcastPreferencePageProps {
  onSubmit: (token: string) => void;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: IPreferenceError | null;
  successContent: ISuccessContent;
  invalidTokenContent: IInvalidTokenContent;
  actionLabel: string;
}

export const BroadcastPreferencePage = ({
  onSubmit,
  isLoading,
  isSuccess,
  isError,
  error,
  successContent,
  invalidTokenContent,
  actionLabel,
}: IBroadcastPreferencePageProps) => {
  const { token } = useParams<{ token: string }>();

  enum ResentStatus {
    Idle = 'idle',
    Success = 'success',
    Error = 'error',
  }

  const [resentStatus, setResentStatus] = useState<ResentStatus>(ResentStatus.Idle);

  useEffect(() => {
    if (token) {
      onSubmit(token);
    }
  }, [token, onSubmit]);

  const isInvalidToken = !token || (isError && error?.cause === 404);
  const invalidTokenMessage = error?.data?.errorMessage || invalidTokenContent.message;
  const errorMessage = error?.message || 'An unexpected error occurred';

  const handleResendOptIn = async () => {
    if (!token) return;
    setResentStatus(ResentStatus.Idle);
    try {
      await publicResendOptIn(token);
      setResentStatus(ResentStatus.Success);
    } catch {
      setResentStatus(ResentStatus.Error);
    }
  };

  if (isLoading) {
    return <PageLayout><PleaseWaitLoader /></PageLayout>;
  }

  if (isSuccess) {
    return (
      <PageLayout>
        <Styled.MessageContainer>
          <Styled.Title>{successContent.title}</Styled.Title>
          <Styled.Message>
            <p>{successContent.message}</p>
            <p><a href={`/broadcast-recipient/${token}/${successContent.linkAction}`}>{successContent.linkText}</a></p>
          </Styled.Message>
        </Styled.MessageContainer>
      </PageLayout>
    );
  }

  if (isInvalidToken) {
    return (
      <PageLayout>
        <Styled.MessageContainer>
          <Styled.Title>{invalidTokenContent.title}</Styled.Title>
          <Styled.ErrorMessage>
            <p>{invalidTokenMessage}</p>
          </Styled.ErrorMessage>
          <Button
            type={ButtonTypes.PRIMARY}
            text="Resend Opt-In"
            data-testid="resend-opt-in-button"
            onClick={handleResendOptIn}
            disabled={
              invalidTokenMessage?.includes('not found') || resentStatus === ResentStatus.Error
            }
          />
          {resentStatus === ResentStatus.Success && (
            <Styled.Message>
              <p>Opt-in Resent</p>
            </Styled.Message>
          )}
          {resentStatus === ResentStatus.Error && (
            <Styled.ErrorMessage>
              <p>An error occurred, this Opt-In has already been resent</p>
            </Styled.ErrorMessage>
          )}
        </Styled.MessageContainer>
      </PageLayout>
    );
  }

  if (isError) {
    return (
      <PageLayout>
        <Styled.MessageContainer>
          <Styled.Title>Something went wrong</Styled.Title>
          <Styled.ErrorMessage>
            <p>We were unable to process your {actionLabel} request. Error: {errorMessage}</p>
          </Styled.ErrorMessage>
          <Styled.Message>
            <p>Please try again later or contact your administrator for assistance.</p>
          </Styled.Message>
        </Styled.MessageContainer>
      </PageLayout>
    );
  }

  return <PageLayout><PleaseWaitLoader /></PageLayout>;
};

const PageLayout = ({ children }: { children: React.ReactNode }) => (
  <Styled.PageWrapper>
    <Styled.ContentArea>
      <Styled.Logo src="/publisherLogo.png" alt="emc Publisher" />
      {children}
    </Styled.ContentArea>
    <Footer hideLinks />
  </Styled.PageWrapper>
);
